import { useState } from "react";
import { Link } from "react-router-dom";
import { ImageSquare, Stack, Package, Printer, MagnifyingGlass } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { ProjectMaterialManager } from "@/components/ProjectMaterialManager";
import { HoldLabelPrint } from "@/components/HoldLabelPrint";

const holdsFor = (m) => {
  const base = {
    kind: m.type === "remnant" ? "remnant" : "slab",
    ref_id: m.id,
    slab_code: m.code,
    color: m.label,
    material: m.material,
    thickness: m.thickness,
    location: m.location,
    length_in: m.length_in,
    width_in: m.width_in,
  };
  if (m.reserved_pieces?.length)
    return m.reserved_pieces.map((pc) => ({
      ...base,
      id: pc.id,
      kind: "slab_piece",
      serial: pc.serial,
      quote_code: pc.held_for,
      at: pc.held_at,
    }));
  return [{ ...base, id: m.id, serial: m.serial, quote_code: (m.via || [])[0] }];
};

const href = (m) =>
  m.type === "remnant" ? `/inventory/remnants/${m.id}` : `/inventory/slabs/${m.id}`;

const STATUS_TONE = {
  Reserved: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  "In Stock": "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  Sold: "border-zinc-700 bg-zinc-800 text-zinc-400",
};

export const ProjectMaterials = ({ materials = [], project, onChanged, children }) => {
  const [manageOpen, setManageOpen] = useState(false);
  const [printing, setPrinting] = useState(null);
  const quoteId = project?.deal?.quote_id || project?.quotes?.[0]?.id || "";
  const labelContext = {
    customer: project?.customer_company || project?.customer_name || "",
    title: project?.name || "Project",
    code: project?.code || "",
    address: project?.job_address || "",
    project_id: project?.id,
    install_date: project?.target_install_date || "",
  };
  return (
  <section
    data-testid="project-materials"
    className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
  >
    <div className="flex items-center gap-2">
      <Stack size={16} weight="bold" className="text-indigo-300" />
      <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-300">
        Materials
      </h2>
      {materials.length > 0 && (
        <span
          data-testid="project-materials-count"
          className="rounded-full bg-indigo-500/20 px-1.5 text-[10px] font-bold text-indigo-300"
        >
          {materials.length}
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {quoteId && (
          <button
            type="button"
            data-testid="project-materials-manage"
            onClick={() => setManageOpen(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-indigo-500/50 bg-indigo-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
          >
            <Package size={13} weight="bold" /> Manage materials
          </button>
        )}
        <button
          type="button"
          data-testid="project-materials-browse"
          onClick={() => setManageOpen(true)}
          title="Browse inventory and reserve a slab for this project"
          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
        >
          <MagnifyingGlass size={13} weight="bold" /> Browse &amp; reserve slabs
        </button>
      </div>
    </div>
    {materials.length === 0 ? (
      <p
        data-testid="project-materials-empty"
        className="mt-3 text-xs text-zinc-500"
      >
        No slab reserved yet — use <span className="text-zinc-300">Browse &amp; reserve slabs</span>{" "}
        to pick one from inventory, or reserve from the quote&apos;s slab layout.
      </p>
    ) : (
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {materials.map((m) => (
          <Link
            key={`${m.type}:${m.id}`}
            to={href(m)}
            data-testid={`project-material-${m.id}`}
            className="flex gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-2.5 transition-colors duration-150 hover:border-indigo-500/60"
          >
            {m.photo ? (
              <img
                src={`${API}/photos/${m.photo}?w=300&auth=${localStorage.getItem("sw_token")}`}
                alt={m.label}
                className="h-16 w-16 shrink-0 rounded-md object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-dashed border-zinc-800">
                <ImageSquare size={20} className="text-zinc-700" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono-data text-[11px] text-indigo-300">
                  {m.code}
                </span>
                {m.type === "remnant" && (
                  <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                    Remnant
                  </span>
                )}
                {m.status && (
                  <span
                    className={`ml-auto rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
                      STATUS_TONE[m.status] || STATUS_TONE.Sold
                    }`}
                  >
                    {m.status}
                  </span>
                )}
              </div>
              <div className="truncate text-sm font-bold text-zinc-100">
                {m.label || "Stone"}
              </div>
              <div className="truncate text-[11px] text-zinc-500">
                {[m.material, m.thickness].filter(Boolean).join(" · ")}
                {m.pieces_total > 0 &&
                  ` · ${m.pieces_reserved}/${m.pieces_total} pieces reserved`}
              </div>
              {m.via?.length > 0 && (
                <div className="mt-0.5 truncate text-[10px] uppercase tracking-widest text-zinc-600">
                  via {m.via.join(", ")}
                </div>
              )}
              <button
                type="button"
                data-testid={`project-material-${m.id}-print-hold`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPrinting(holdsFor(m));
                }}
                className="mt-1.5 inline-flex h-7 items-center gap-1 rounded-md border border-amber-500/40 px-2 text-[10px] font-bold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-500/20"
              >
                <Printer size={12} weight="bold" /> Hold label
              </button>
            </div>
          </Link>
        ))}
      </div>
    )}
    {printing && (
      <HoldLabelPrint
        holds={printing}
        context={labelContext}
        onClose={() => setPrinting(null)}
        testId="project-material-hold-label"
      />
    )}
    {children}
    <ProjectMaterialManager
      quoteId={quoteId}
      projectId={project?.id}
      project={project}
      open={manageOpen}
      onClose={() => setManageOpen(false)}
      onChanged={() => onChanged?.()}
    />
  </section>
  );
};
